import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import {
  useCancelSessionSlotMutation,
  useGetSessionByIdQuery,
} from "@/services/trainingApi";
import { useGetMemberByIdQuery } from "@/services/memberApi";
import { ErrorType } from "@/types";
import {
  Calendar,
  Clock,
  User,
  Dumbbell,
  MapPin,
  Mail,
  Phone,
  XCircle,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { parseSessionDateTime } from "@/lib/helper";
import { RescheduleModal } from "@/components/admin/training/series/reschedule-modal";

interface TrainingSessionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Record<string, any> | null;
  coachName?: string;
  orgId: number;
  onSuccess?: () => void;
}

export const TrainingSessionSheet: React.FC<TrainingSessionSheetProps> = ({
  isOpen,
  onClose,
  booking,
  coachName,
  orgId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [cancelSessionSlot, { isLoading: isCancelling }] =
    useCancelSessionSlotMutation();

  const memberId = booking?.member_id;
  const { data: memberData } = useGetMemberByIdQuery(
    { org_id: orgId, id: Number(memberId) },
    { skip: !memberId || !isOpen }
  );

  const memberInfo = (memberData as any)?.data || memberData;

  const sessionId =
    booking?.training_session_id ||
    booking?.consumed_booking?.training_session_id ||
    booking?.consumed_slots?.[0]?.training_session_id;

  const { data: sessionData } = useGetSessionByIdQuery(Number(sessionId), {
    skip: !sessionId || !isOpen,
  });

  const packageName =
    booking?.training_package_name ||
    booking?.facility_name ||
    "Personal Training";

  const sessionNumber =
    booking?.session_number ||
    booking?.consumed_booking?.session_number ||
    "1";

  // Formatted date and time
  const startDate = booking?.slot_start_time
    ? parseSessionDateTime(booking.slot_start_time)
    : booking?.booking_date
    ? parseSessionDateTime(booking.booking_date)
    : null;

  const formattedDate =
    startDate && !isNaN(startDate.getTime())
      ? format(startDate, "EEEE, MMMM dd, yyyy")
      : booking?.booking_date || "N/A";

  const startParsed = booking?.slot_start_time
    ? parseSessionDateTime(booking.slot_start_time)
    : null;
  const startTimeStr = startParsed
    ? format(startParsed, "hh:mm a")
    : booking?.start_time || "";

  const endParsed = booking?.slot_end_time
    ? parseSessionDateTime(booking.slot_end_time)
    : null;
  const endTimeStr = endParsed
    ? format(endParsed, "hh:mm a")
    : booking?.end_time || "";

  const timeRange =
    startTimeStr && endTimeStr
      ? `${startTimeStr} – ${endTimeStr}`
      : startTimeStr || "N/A";

  const handleCancelClass = async () => {
    if (!sessionId) {
      toast({
        variant: "destructive",
        title: "Cannot cancel session",
        description: "Training session ID not found for this booking.",
      });
      return;
    }

    try {
      await cancelSessionSlot(sessionId).unwrap();
      toast({
        variant: "success",
        title: "Training class cancelled",
        description:
          "The session credit has been refunded to the training package and the session is ready to be rescheduled.",
      });
      setIsConfirmOpen(false);
      onClose();
      onSuccess?.();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description: `${
          typedError.data?.detail ||
          typedError.data?.message ||
          "Something went wrong while cancelling this training class."
        }`,
      });
    }
  };

  if (!booking) return null;

  const effectiveCoach =
    coachName ||
    booking?.coach_name ||
    booking?.coach?.name ||
    "Assigned Coach";

  const memberDisplayName =
    booking?.member_name ||
    (memberInfo
      ? [memberInfo.first_name, memberInfo.last_name].filter(Boolean).join(" ")
      : "Member");

  const memberEmail = memberInfo?.email || booking?.member_email || "N/A";
  const memberPhone =
    memberInfo?.mobile_number ||
    memberInfo?.phone ||
    booking?.member_phone ||
    "N/A";

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="w-full sm:max-w-md md:max-w-lg overflow-y-auto p-6 flex flex-col justify-between">
          <div className="space-y-6">
            <SheetHeader className="text-left space-y-1">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-xl font-bold flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-primary" />
                  Training Class Details
                </SheetTitle>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  {(booking?.status || "Approved").toUpperCase()}
                </Badge>
              </div>
              <SheetDescription className="text-xs text-muted-foreground">
                Personal training booking created from the Training Module
              </SheetDescription>
            </SheetHeader>

            <Separator />

            {/* Member Details Card */}
            <Card className="border border-border/80 shadow-xs">
              <CardHeader className="py-3 px-4 bg-muted/40 border-b">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <User className="h-4 w-4 text-primary" />
                  Client Information
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-base">
                    {memberDisplayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm capitalize">
                      {memberDisplayName}
                    </h4>
                    {memberInfo?.own_member_id && (
                      <span className="text-xs text-muted-foreground">
                        ID: {memberInfo.own_member_id}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{memberEmail}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    <span>{memberPhone}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Training Class Details Card */}
            <Card className="border border-border/80 shadow-xs">
              <CardHeader className="py-3 px-4 bg-muted/40 border-b">
                <div className="flex items-center justify-between font-semibold text-sm">
                  <div className="flex items-center gap-2">
                    <Dumbbell className="h-4 w-4 text-primary" />
                    Session Information
                  </div>
                  <Badge variant="outline" className="font-semibold text-xs">
                    Session #{sessionNumber}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">
                    Package Name
                  </span>
                  <p className="font-medium text-foreground">{packageName}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" /> Trainer / Coach
                    </span>
                    <p className="font-medium capitalize text-xs mt-0.5">
                      {effectiveCoach}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Facility / Room
                    </span>
                    <p className="font-medium capitalize text-xs mt-0.5">
                      {booking?.facility_name || "Facility"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 pt-2 border-t">
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Scheduled Date
                    </span>
                    <p className="font-medium text-xs mt-0.5">{formattedDate}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Session Time
                    </span>
                    <p className="font-medium text-xs mt-0.5">{timeRange}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reschedule & Credit Refund Info Box */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2.5 text-xs text-amber-900">
              <RotateCcw className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Cancellation Policy:</span>
                <p className="mt-0.5 text-amber-800 leading-relaxed">
                  Cancelling will revert 1 session credit back to the client's
                  package and free up the session to be rescheduled at any time.
                </p>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-6 border-t mt-6 flex flex-wrap gap-2.5">
            <Button
              variant="outline"
              className="flex-1 min-w-[90px] border-gray-300"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="outline"
              className="flex-1 min-w-[120px] border-primary text-primary hover:bg-primary/10 font-semibold flex items-center justify-center gap-1.5"
              onClick={() => setIsRescheduleOpen(true)}
              disabled={!sessionId}
            >
              <RotateCcw className="h-4 w-4" />
              Reschedule
            </Button>
            <Button
              variant="destructive"
              className="flex-1 min-w-[120px] font-semibold flex items-center justify-center gap-1.5"
              onClick={() => setIsConfirmOpen(true)}
              disabled={isCancelling || !sessionId}
            >
              <XCircle className="h-4 w-4" />
              Cancel Class
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Reschedule Modal */}
      <RescheduleModal
        open={isRescheduleOpen}
        onOpenChange={setIsRescheduleOpen}
        session={
          sessionId
            ? ({
                id: Number(sessionId),
                session_number: Number(sessionNumber),
                start_time:
                  sessionData?.start_time ||
                  booking?.slot_start_time ||
                  booking?.start_time ||
                  (booking?.booking_date
                    ? `${booking.booking_date}T${booking.start_time || "00:00:00"}`
                    : undefined),
                facility_id: sessionData?.facility_id || booking?.facility_id,
                coach_id: sessionData?.coach_id || booking?.coach_id,
                training_package: {
                  reschedule_cutoff_hours:
                    sessionData?.training_package?.reschedule_cutoff_hours ??
                    booking?.reschedule_cutoff_hours ??
                    booking?.training_package?.reschedule_cutoff_hours ??
                    48,
                },
                ...(sessionData || {}),
                ...booking,
              } as any)
            : null
        }
        onSuccess={() => {
          onSuccess?.();
          onClose();
        }}
      />

      {/* Confirmation Dialog */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <div className="flex flex-col items-center justify-center gap-2 mb-2">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <AlertDialogTitle className="text-lg font-bold text-center">
                Cancel Training Class?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-center text-sm text-muted-foreground">
              Are you sure you want to cancel{" "}
              <strong className="text-foreground">
                Session #{sessionNumber}
              </strong>{" "}
              for <strong className="text-foreground">{memberDisplayName}</strong>
              ?
              <br />
              <br />
              This will refund the session credit to their package and free up
              the session to be rescheduled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3 mt-4">
            <AlertDialogCancel
              onClick={() => setIsConfirmOpen(false)}
              className="w-full font-semibold"
            >
              Keep Class
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelClass}
              className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold"
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Yes, Cancel Class"}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
